

"use client";

import { Button } from "@/components/ui/button";
import { useUser } from "@/context/user-context";
import { sanitizeObjectDeep } from "@/lib/utils";
import { CreateCommentAction, fetchCommentsAction } from "@/server-actions/comment-actions";
import { CommentInput, Enum_Comment_Commentstatus } from "@/types/generated";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Comment,
  CommentForm,
  CommentSectionProps,
  formSchema,
} from "./commentForm";
import { CommentHeader } from "./commentHeader";
import { CommentList } from "./commentList";


export default function CommentSection({
  article,
  initialComments = [],
}: CommentSectionProps) {
  const router = useRouter();
  const [comments, setComments] = useState<Comment[]>(() =>
    initialComments.filter(
      (comment) =>
        comment.commentStatus === Enum_Comment_Commentstatus.Published &&
        comment.approved === true
    )
  );
  const [loading, setLoading] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
  const { token, user } = useUser();

  const fetchComments = async () => {
    setLoading(true);
    const res = await fetchCommentsAction(article, sortBy);
    if (res.success && res.data) {
      setComments(res.data);
    } else {
      toast.error(res.message || "Failed to load comments");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchComments();
  }, [sortBy]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (!token) {
        toast.error("Please sign in to post comments");
        return;
      }

      const trimmedContent = values.content.trim();
      if (!trimmedContent) {
        toast.error("Comment cannot be empty or just spaces.");
        return;
      }

      const input: CommentInput = {
        content: trimmedContent,
        article: article.documentId,
        approvedBy: user?.documentId,
        ...(values.parentComment && {
          parentComment: values.parentComment,
        }),
        commentBy: user?.documentId,
      };

      const cleanedData = sanitizeObjectDeep(input);
      const res = await CreateCommentAction(cleanedData);

      if (!res.success) {
        toast.error(typeof res.message === "string" ? res.message : "Failed to post comment.");
        return;
      }

      const newComment: Comment = {
        documentId: res.data?.documentId || "",
        content: res.data?.content || "",
        commentStatus: Enum_Comment_Commentstatus.Published,
        approvedBy: {
          username: user?.username || "You",
          name: user?.name || "You",
          documentId: user?.documentId,
          avatar: { url: user?.avatar?.url || "" },
        },
        commentBy: {
          username: user?.username || "You",
          name: user?.name || "You",
          documentId: user?.documentId,
          avatar: { url: user?.avatar?.url || "" },
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        approved: true,
        parentComment: values.parentComment ? { documentId: values.parentComment } : undefined,
      };

      toast.success("Success!", {
        description: values.parentComment ? "Reply posted successfully!" : "Comment posted successfully!",
      });

      if (values.parentComment) {
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.documentId === values.parentComment) {
              return {
                ...comment,
                replies: [...(comment.replies || []), newComment],
              };
            }
            if (comment.replies) {
              return {
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply.documentId === values.parentComment
                    ? { ...reply, replies: [...(reply.replies || []), newComment] }
                    : reply
                ),
              };
            }
            return comment;
          })
        );
      } else {
        setComments((prev) => [newComment, ...prev]);
      }

      setReplyingTo(null);
    } catch (error) {
      toast.error("An unexpected error occurred");
    }
  };

  const startReply = (commentId: string) => setReplyingTo(commentId);
  const cancelReply = () => setReplyingTo(null);

  const visibleComments = comments.filter(
    (comment) =>
      !comment.parentComment &&
      (comment.commentStatus === Enum_Comment_Commentstatus.Published ||
        (token && user?.documentId === comment.approvedBy.documentId))
  );

  return (
    <div className="container mx-auto max-w-3xl">
      <div className="w-full max-w-3xl mx-auto">
        <CommentHeader
          loading={loading}
          visibleCommentsCount={visibleComments.length}
          onRefresh={fetchComments}
          sortBy={sortBy}
          toggleSort={() => setSortBy(sortBy === "newest" ? "oldest" : "newest")}
        />

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin h-6 w-6" />
          </div>
        ) : (
          <div className="space-y-6">
            {token ? (
              <CommentForm
                articleId={article.documentId}
                user={user}
                token={token}
                onSubmit={onSubmit}
              />
            ) : (
              <div className="rounded-lg border p-4 text-center bg-muted/50">
                <p className="text-muted-foreground mb-2">
                  Please sign in to leave a comment
                </p>
                <Button variant="default" size="sm" onClick={() => router.push("/login")}>
                  Sign In
                </Button>
              </div>
            )}

            {visibleComments.length > 0 ? (
              <CommentList
                level={1}
                comments={comments}
                token={token}
                user={user}
                viewAll={viewAll}
                setViewAll={setViewAll}
                onReply={startReply}
                onSubmitReply={onSubmit}
                replyingTo={replyingTo}
                cancelReply={cancelReply}
              />
            ) : (
              <div className="rounded-lg border p-8 text-center bg-muted/50">
                <p className="text-muted-foreground">
                  No comments yet. Be the first to share your thoughts!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
